import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Check, Home, Loader2, AlertTriangle, RotateCw, FileText } from "lucide-react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { api } from "@/lib/api";
import { takePendingSubmit } from "@/lib/pendingSubmits";

/** Resolve the background submit promise from the wizard. If we don't have one
 *  (page reload / direct navigation), fall back to polling the project status. */
export default function ThankYou() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const draftToken = location.state?.draftToken || params.get("p");
  const [submitPromise] = useState(() => (draftToken ? takePendingSubmit(draftToken) : undefined));

  // 'pending' while the background submit hasn't resolved, 'ok' on success,
  // 'error' if the POST failed (with retry button).
  const [state, setState] = useState(submitPromise ? "pending" : "ok");
  const [errorDetail, setErrorDetail] = useState("");
  const [retrying, setRetrying] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (submitPromise) {
      submitPromise.then((result) => {
        if (cancelled) return;
        if (result.ok) setState("ok");
        else {
          setErrorDetail(typeof result.detail === "string" ? result.detail : "Submission failed");
          setState("error");
        }
      });
      return () => { cancelled = true; };
    }
    // No promise → user reloaded. Verify the project did submit by polling status.
    if (draftToken) {
      api.get(`/projects/draft/${draftToken}`)
        .then((r) => {
          if (cancelled) return;
          const s = (r.data?.status || "").toLowerCase();
          if (s === "submitted" || s === "in review" || s === "written" || s === "published") {
            setState("ok");
          }
        })
        .catch(() => {});
    }
    return () => { cancelled = true; };
  }, [submitPromise, draftToken]);

  const retry = async () => {
    if (!draftToken) return navigate("/");
    setRetrying(true);
    try {
      await api.post(`/projects/draft/${draftToken}/submit`, { hp_website: "" });
      setState("ok");
      toast.success("Submission sent through.");
    } catch (e) {
      const d = e?.response?.data?.detail;
      const status = e?.response?.status;
      // If submit endpoint says it's already submitted, that's success.
      if (status === 400 && /already.*submitted|not.*draft/i.test(String(d || ""))) {
        setState("ok");
      } else {
        setErrorDetail(typeof d === "string" ? d : "Submission failed");
      }
    } finally { setRetrying(false); }
  };

  return (
    <Layout>
      <div className="max-w-xl mx-auto px-4 py-16 sm:py-24 text-center" data-testid="thank-you-page">
        {state === "pending" && <Pending />}
        {state === "ok" && <Success />}
        {state === "error" && (
          <ErrorState detail={errorDetail} onRetry={retry} retrying={retrying} draftToken={draftToken} />
        )}
      </div>
    </Layout>
  );
}

function Pending() {
  return (
    <div data-testid="thank-you-pending">
      <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-amber-50 text-amber-600">
        <Loader2 className="h-7 w-7 animate-spin" />
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
        Sending your submission…
      </h1>
      <p className="mt-3 text-gray-600">
        Hang tight — this usually takes a few seconds. Don't refresh just yet.
      </p>
    </div>
  );
}

function Success() {
  return (
    <div data-testid="thank-you-ok">
      <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-emerald-50 text-emerald-600">
        <Check className="h-7 w-7" />
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
        Submitted ✓
      </h1>
      <p className="mt-3 text-gray-600">
        Thank you. A PDF copy has been emailed to you and to Marc.
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-2 justify-center">
        <Link to="/" className="rcw-btn-primary" data-testid="thank-you-home">
          <Home className="h-4 w-4" /> Back to home
        </Link>
        <Link to="/admin" className="rcw-btn-ghost" data-testid="thank-you-projects">
          <FileText className="h-4 w-4" /> View all projects
        </Link>
      </div>
    </div>
  );
}

function ErrorState({ detail, onRetry, retrying, draftToken }) {
  return (
    <div data-testid="thank-you-error">
      <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-red-50 text-red-600">
        <AlertTriangle className="h-7 w-7" />
      </div>
      <h1 className="mt-6 font-display text-3xl sm:text-4xl font-bold tracking-tight text-gray-900">
        Couldn't send it through
      </h1>
      <p className="mt-3 text-gray-600">
        Your draft is safe and still saved. {detail ? <span className="block mt-2 text-sm text-red-700">{detail}</span> : null}
      </p>
      <div className="mt-8 flex flex-col sm:flex-row gap-2 justify-center">
        <button onClick={onRetry} disabled={retrying} className="rcw-btn-amber" data-testid="thank-you-retry">
          {retrying ? <Loader2 className="h-4 w-4 animate-spin" /> : <RotateCw className="h-4 w-4" />} Try again
        </button>
        {draftToken && (
          <Link to={`/draft/${draftToken}`} className="rcw-btn-ghost" data-testid="thank-you-resume">
            <FileText className="h-4 w-4" /> Open the draft
          </Link>
        )}
      </div>
    </div>
  );
}
